import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// A single place to know "does this customer have a payment that still needs a session time?" - used
// by the site-wide banner, the header/hero CTA swap, and the login/booking-entry redirect, so "you paid,
// please come back and finish" is communicated everywhere rather than only on the payments page.
const CACHE_MS = 30 * 1000;
const CHANGED_EVENT = 'bv:pending-booking-changed';
let cached = null; // { data, at }
let inflight = null;

const authHeaders = () => {
  const token = localStorage.getItem('loginToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Resolves to the pending payment ({ orderId, amount, session, ... }) or null.
export const fetchPendingBooking = async ({ force = false } = {}) => {
  if (!localStorage.getItem('loginToken')) return null;
  if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.data;
  if (!force && inflight) return inflight;

  inflight = axios
    .post(`${process.env.REACT_APP_SERVER_URL}/my-payments/pending`, {}, { headers: authHeaders() })
    .then(({ data }) => {
      cached = { data: data?.pending || null, at: Date.now() };
      return cached.data;
    })
    .catch(() => {
      cached = { data: null, at: Date.now() };
      return null;
    })
    .finally(() => { inflight = null; });
  return inflight;
};

// Cleared whenever the customer's booking status may have changed on the server (logging in,
// finishing a booking): also tells every mounted usePendingBooking (e.g. the site-wide banner,
// already sitting there since before login) to refetch right away rather than wait out its cache.
export const clearPendingBookingCache = () => {
  cached = null;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGED_EVENT));
};

export const usePendingBooking = () => {
  const [pending, setPending] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(!cached);

  const refresh = useCallback(async (force = true) => {
    setLoading(true);
    const data = await fetchPendingBooking({ force });
    setPending(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchPendingBooking({});
      if (!cancelled) {
        setPending(data);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onChange = () => { refresh(true); };
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, [refresh]);

  return { pending, loading, refresh };
};

export const completeBookingPath = (orderId) => `/complete-booking/${encodeURIComponent(orderId)}`;
